import { startRegistration, startAuthentication, browserSupportsWebAuthn } from "@simplewebauthn/browser";
import { supabase } from "@/integrations/supabase/client";

export { browserSupportsWebAuthn };

export function isInIframe(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

export function canUseWebAuthn(): boolean {
  return browserSupportsWebAuthn() && !isInIframe();
}

export async function registerPasskey(deviceName?: string) {
  const { data: optionsData, error: optionsError } = await supabase.functions.invoke("webauthn-register-options");
  if (optionsError) throw new Error("Failed to get registration options");

  const { options } = optionsData;

  const credential = await startRegistration({ optionsJSON: options });

  const { data: verifyData, error: verifyError } = await supabase.functions.invoke("webauthn-register-verify", {
    body: { credential, deviceName },
  });
  if (verifyError) throw new Error("Failed to verify registration");

  return verifyData;
}

export async function loginWithPasskey(email: string) {
  const { data: optionsData, error: optionsError } = await supabase.functions.invoke("webauthn-login-options", {
    body: { email },
  });
  if (optionsError || optionsData?.error) {
    throw new Error(optionsData?.error || "Failed to get login options");
  }

  const { options, userId } = optionsData;

  const credential = await startAuthentication({ optionsJSON: options });

  const { data: verifyData, error: verifyError } = await supabase.functions.invoke("webauthn-login-verify", {
    body: { credential, userId },
  });
  if (verifyError || !verifyData?.verified) {
    throw new Error("Biometric verification failed");
  }

  const { session } = verifyData;
  await supabase.auth.setSession({
    access_token: session.access_token,
    refresh_token: session.refresh_token,
  });

  return session;
}

/**
 * Discoverable / conditional UI passkey login.
 * - When `conditional` is true, the browser silently waits and offers the passkey
 *   via the email field's autofill UI (requires autocomplete="username webauthn").
 * - When false, triggers an immediate modal biometric prompt (requires user gesture).
 */
export async function loginWithPasskeyDiscoverable(opts: { conditional?: boolean; signal?: AbortSignal } = {}) {
  const { conditional = false, signal } = opts;

  const { data: optionsData, error: optionsError } = await supabase.functions.invoke("webauthn-login-options", {
    body: { discoverable: true },
  });
  if (optionsError || optionsData?.error) {
    throw new Error(optionsData?.error || "Failed to get login options");
  }

  const { options } = optionsData;

  const credential = await startAuthentication({
    optionsJSON: options,
    useBrowserAutofill: conditional,
    // @ts-expect-error - signal is passed through to navigator.credentials.get
    signal,
  });

  const { data: verifyData, error: verifyError } = await supabase.functions.invoke("webauthn-login-verify", {
    body: { credential, discoverable: true },
  });
  if (verifyError || !verifyData?.verified) {
    throw new Error("Biometric verification failed");
  }

  const { session } = verifyData;
  await supabase.auth.setSession({
    access_token: session.access_token,
    refresh_token: session.refresh_token,
  });

  return session;
}

/**
 * Returns true if the browser supports WebAuthn Conditional UI (autofill-driven passkeys).
 */
export async function supportsConditionalUI(): Promise<boolean> {
  try {
    const PKC = (globalThis as any).PublicKeyCredential;
    if (!PKC || typeof PKC.isConditionalMediationAvailable !== "function") {
      return false;
    }
    return await PKC.isConditionalMediationAvailable();
  } catch {
    return false;
  }
}
